import { useParams, Link } from "react-router";
import { articles } from "../data/articles";
import { Calendar, Clock, ArrowLeft, User, Share2 } from "lucide-react";
import { motion } from "motion/react";
import { useEffect } from "react";

export function ArticleDetailPage() {
  const { slug } = useParams();
  const article = articles.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 pt-20">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Article Not Found</h1>
        <p className="text-slate-600 mb-8">The article you are looking for does not exist.</p>
        <Link to="/articles" className="px-6 py-3 bg-[#FD5750] text-white rounded-lg font-bold">
          Back to Articles
        </Link>
      </div>
    );
  }

  return (
    <article className="pt-20 bg-white min-h-screen">
      {/* Article Header */}
      <div className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
           <img src={article.image} alt="Background" className="w-full h-full object-cover blur-sm opacity-50" />
           <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10 max-w-4xl">
          <Link to="/articles" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-8 transition-colors">
            <ArrowLeft size={20} /> Back to Articles
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-6">
               <span className="px-3 py-1 bg-[#FD5750] text-white text-xs font-bold rounded-full uppercase tracking-wide">
                 {article.category}
               </span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300 border-t border-slate-700/50 pt-6">
               <div className="flex items-center gap-2">
                 <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                   <User size={16} />
                 </div>
                 <div>
                   <p className="font-semibold text-white">{article.author}</p>
                   <p className="text-xs text-slate-400">{article.authorRole}</p>
                 </div>
               </div>
               
               <div className="flex items-center gap-2">
                 <Calendar size={16} />
                 {article.date}
               </div>
               
               <div className="flex items-center gap-2">
                 <Clock size={16} />
                 {article.readTime}
               </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Article Content */}
      <div className="container mx-auto px-6 py-16 max-w-3xl">
        <motion.div
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="prose prose-lg prose-slate max-w-none">
             <p className="text-xl text-slate-600 font-medium leading-relaxed mb-8 border-l-4 border-[#FD5750] pl-6 italic">
               {article.excerpt}
             </p>
             
             {/* Render HTML content safely since we trust our internal data */}
             <div dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
          
          {/* Share & Tags */}
          <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-between">
             <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">Share this article:</span>
                <button className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors">
                  <Share2 size={18} />
                </button>
             </div>
          </div>
        </motion.div>
      </div>
      
      {/* Related/More Articles CTA */}
      <div className="bg-slate-50 py-16 border-t border-slate-200">
         <div className="container mx-auto px-6 max-w-4xl text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Enjoyed this read?</h3>
            <p className="text-slate-600 mb-8">Explore more insights and strategies in our Knowledge Hub.</p>
            <Link to="/articles" className="inline-flex items-center justify-center px-8 py-3 bg-white text-slate-900 border border-slate-300 font-bold rounded-lg hover:border-[#FD5750] hover:text-[#FD5750] transition-all">
               View All Articles
            </Link>
         </div>
      </div>
    </article>
  );
}
